<template></template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue';
import { debounce } from 'lodash';
import { useToast } from 'vue-toast-notification';
import { $emitter, type ToastOptions, type ToastPayload } from '@/services/emitter';

type ToastTheme = 'error' | 'success' | 'warning';

const toast = useToast();

let collectedMessages: Partial<Record<ToastTheme, string[]>> = {};

const showMessageDebounced = debounce((messages: string[], options: Record<string, unknown>) => {
  const message = messages.join('<br>');
  messages.length = 0;

  toast.open({
    message,
    duration: 5000,
    ...options,
  });
}, 350);

const showMessage = (theme: ToastTheme, message: string, options: ToastOptions = {}) => {
  collectedMessages[theme] = Array.from(
    new Set([...(collectedMessages[theme] || []), message]),
  );

  if (collectedMessages[theme]?.length) {
    showMessageDebounced(collectedMessages[theme] as string[], {
      type: theme,
      ...options,
    });
  }
};

const buildDontHideOptions = (options: ToastOptions) => ({
  ...options,
  ...(options.dontHide && {
    className: 'dontHide',
    duration: 600000,
    action: {
      text: '',
      class: 'toasted__close far fa-times v-icon',
      onClick: (_event: unknown, toastObject: { goAway: (delay: number) => void }) => {
        toastObject.goAway(0);
      },
    },
  }),
});

const onToastError = ({ message, options = {} }: ToastPayload) => {
  showMessage('error', message, buildDontHideOptions(options));
};

const onToastSuccess = ({ message, options = {} }: ToastPayload) => {
  showMessage('success', message, {
    ...options,
    duration: 2000,
  });
};

const onToastWarning = ({ message, options = {} }: ToastPayload) => {
  showMessage('warning', message, buildDontHideOptions(options));
};

const onToastClear = () => {
  toast.clear();
};

onMounted(() => {
  $emitter.on('toast-error', onToastError);
  $emitter.on('toast-success', onToastSuccess);
  $emitter.on('toast-warning', onToastWarning);
  $emitter.on('toast-clear', onToastClear);
});

onBeforeUnmount(() => {
  $emitter.off('toast-error', onToastError);
  $emitter.off('toast-success', onToastSuccess);
  $emitter.off('toast-warning', onToastWarning);
  $emitter.off('toast-clear', onToastClear);
  showMessageDebounced.cancel();
});
</script>

<style lang="scss">
.v-toast p {
  margin-bottom: 0 !important;
}
.alert-success-block {
  display: none !important;
  border-width: 0 !important;
  border-radius: 5px !important;
  position: absolute !important;
  right: 50px;
  bottom: 50px;
  z-index: 10;
}

.toasted-container.bottom-right,
.v-toast.v-toast--bottom-right {
  left: 5%;
  align-items: flex-end;
  @media only screen and (max-width: 600px) {
    left: 0 !important;
    right: 0 !important;
  }
}

.toasted,
.v-toast__item {
  border-radius: 5px !important;
  font-weight: 500 !important;
  padding: 6px 20px !important;
  color: var(--color-white);
  min-width: 280px !important;
  max-width: 25% !important;
  min-height: 45px !important;
  font-size: 14px !important;
  overflow: auto;
  max-height: calc(100vh - 100px);
  opacity: 1 !important;
  @media only screen and (max-width: 600px) {
    max-width: 75% !important;
    min-width: auto !important;
  }
  .action {
    margin-left: 32px !important;
    background: var(--color-white);
    border: solid 1px var(--color-gray20);
    color: var(--color-black) !important;
    border-radius: 5px !important;
    font-size: 13px !important;
    text-transform: capitalize !important;
    height: 34px !important;
    margin: 4px 8px;
    background-image: linear-gradient(0deg, var(--color-gray10) 0px, var(--color-gray5)) !important;
    &:hover {
      text-decoration: none !important;
    }
  }
}
.toasted.success,
.v-toast__item--success {
  background: var(--color-green) !important;
}
.toasted.bubble,
.v-toast__item--error,
.v-alert.error {
  background: var(--color-red) !important;
  .fa-times-circle {
    color: var(--color-white);
  }
}
.toasted.warning,
.v-toast__item--warning {
  background: var(--color-tangerine) !important;
  color: var(--color-white) !important;
}

.toasted.dontHide {
  padding-right: 25px !important;
  .toasted__close {
    border: none;
    background: none !important;
    position: absolute;
    top: 5px;
    right: 5px;
    margin: 0 !important;
    width: 0 !important;
    height: 0 !important;
    &:before {
      color: #fff;
    }
  }
}

.v-alert {
  box-shadow: 0 1px 3px var(--color-black10);
  z-index: 10;
  border-radius: 5px !important;
  font-weight: 500 !important;
  color: var(--color-white);
  border-width: 0 !important;
  padding: 16px 16px 16px !important;
  div {
    overflow: hidden;
    p {
      margin-bottom: 8px;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
